import React, { useState } from 'react';
import { Box, Alert, Typography, Fab } from '@mui/material';
import * as Icons from '../constants/icons';
import { PAGE_FILL_HEIGHT } from '../constants/layout';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import LoadingView from '../components/LoadingView';
import { useMonthlyTrialBalance } from '../hooks/useMonthlyTrialBalance';
import MonthlyTrialBalanceFilterHeader from '../components/trialBalance/MonthlyTrialBalanceFilterHeader';
import MonthlyTrialBalanceTable from '../components/trialBalance/MonthlyTrialBalanceTable';
import JournalLedgerDialog, { type LedgerCellContext } from '../components/trialBalance/JournalLedgerDialog';
import AccountingAssistantDialog from '../components/trialBalance/AccountingAssistantDialog';

/**
 * 月次合計試算表ページ。
 *
 * 指定した会計年度・月の PL（損益）科目を合計試算表形式（前月繰越/借方/貸方/残高）で表示する。
 * 勘定科目の金額をクリックすると、配下の補助科目内訳（補助元帳の段階1）を展開する。
 * ヘッダー（年度・拠点・部署フィルタ）は常時表示し、テーブル領域のみローディング/エラーを切り替える。
 */
export default function MonthlyTrialBalanceManagement() {
  const tb = useMonthlyTrialBalance();
  // 補助元帳ダイアログの対象セル（null = 閉じている）
  const [ledgerContext, setLedgerContext] = useState<LedgerCellContext | null>(null);
  // 会計AIアシスタント・モーダルの開閉
  const [assistantOpen, setAssistantOpen] = useState(false);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', height: PAGE_FILL_HEIGHT, boxSizing: 'border-box', overflow: 'hidden' }}>
      <Box sx={{ width: '92%', mt: 0, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
        <MonthlyTrialBalanceFilterHeader tb={tb} />

        {/* ⚠️ エラー / データなし */}
        {tb.error && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>{tb.error}</Alert>
        )}

        {/* 📊 コンテンツ領域（テーブル / 空状態）。ローディングはこの上にオーバーレイ表示しレイアウトを動かさない */}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* 合計試算表テーブル */}
          {tb.rows.length > 0 && (
            <MonthlyTrialBalanceTable
              rows={tb.rows}
              expandedSections={tb.expandedSections}
              toggleSection={tb.toggleSection}
              expandedAccounts={tb.expandedAccounts}
              toggleAccount={tb.toggleAccount}
              onCellClick={setLedgerContext}
            />
          )}

          {/* 📭 初期状態でデータなし（エラーもローディングもない） */}
          {!tb.loading && !tb.error && tb.rows.length === 0 && (
            <Box sx={{ mt: 6, p: 5, border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
              <Typography color="textSecondary">データが存在しません。同期を実行するか期間を確認してください。</Typography>
            </Box>
          )}

          {/* ⏳ 進行状況オーバーレイ（絶対配置でテーブルに重ね、レイアウトをずらさない） */}
          {tb.loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.65)',
                borderRadius: '12px',
                backdropFilter: 'blur(1px)',
              }}
            >
              <LoadingView message="データ更新中..." height="auto" size={50} />
            </Box>
          )}
        </Box>
      </Box>

      {/* 📒 補助元帳ドリルダウンダイアログ（金額セルクリックで開く） */}
      <JournalLedgerDialog
        context={ledgerContext}
        fiscalYear={tb.fiscalYear}
        branchCodes={tb.selectedBranchCodes}
        departmentCodes={tb.selectedDepartmentCodes}
        onClose={() => setLedgerContext(null)}
      />

      {/* 🤖 会計AIアシスタント起動ボタン（右下固定）＋モーダル */}
      <Fab
        variant="extended"
        onClick={() => setAssistantOpen(true)}
        sx={{ ...STANDARD_FAB_CTA_SX, position: 'fixed', bottom: 28, right: 28, zIndex: 30 }}
      >
        <Icons.AutoAwesome sx={{ mr: 1 }} /> 会計AIに質問
      </Fab>
      <AccountingAssistantDialog open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </Box>
  );
}
